import {useMemo, useState} from 'react';
import {createBot} from '../../services/developerService';
import {
  canScheduleVideoCallLink,
  isSupportedVideoCallLink,
} from '../../common/videoCallLinks';
import {
  SCHEDULED_BOT_MAX_LEAD_MS,
  SCHEDULE_DATE_WINDOW_DAYS,
  buildDateOptions,
  combineJoinAtLocal,
  formatForDatetimeLocal,
  getUserTimezoneLabel,
  splitJoinAtLocal,
} from '../../common/scheduling';
import {
  CREATE_SESSION_LAUNCH_ACTIONS,
  CREATE_SESSION_MIN_SCHEDULE_LEAD_MS,
  buildCreateSessionLaunchIntent,
  defaultCreateSessionJoinAt,
} from './createSessionFormUtils';

export default function useCreateSessionSchedule({
  draft,
  ensureValidDraft,
  onDraftAccepted,
  onScheduleClose,
  onScheduleOpen,
  onScheduled,
  toast,
}: Record<string, any>) {
  const [scheduleJoinAtLocal, setScheduleJoinAtLocal] = useState('');
  const [hasEditedSchedule, setHasEditedSchedule] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  const dateOptions = useMemo(
    () => buildDateOptions(SCHEDULE_DATE_WINDOW_DAYS),
    [],
  );
  const userTimezoneLabel = useMemo(() => getUserTimezoneLabel(), []);
  const {date: scheduledDateKey, time: scheduledTimeKey} =
    splitJoinAtLocal(scheduleJoinAtLocal);

  const scheduleError = useMemo(() => {
    if (!scheduleJoinAtLocal) return 'Pick a date and time.';
    const parsed = new Date(scheduleJoinAtLocal);
    if (Number.isNaN(parsed.getTime())) return 'Pick a valid date and time.';
    const diff = parsed.getTime() - Date.now();
    if (diff < CREATE_SESSION_MIN_SCHEDULE_LEAD_MS) {
      return 'Schedule at least 30 minutes from now.';
    }
    if (diff > SCHEDULED_BOT_MAX_LEAD_MS) {
      return 'Schedule within the next 30 days.';
    }
    return null;
  }, [scheduleJoinAtLocal]);
  const visibleScheduleError = hasEditedSchedule ? scheduleError : null;

  const openScheduleModal = () => {
    if (!ensureValidDraft()) return;
    onDraftAccepted?.();
    setHasEditedSchedule(false);
    setScheduleJoinAtLocal(
      formatForDatetimeLocal(defaultCreateSessionJoinAt()),
    );
    onScheduleOpen();
  };

  const handleScheduleSubmit = async () => {
    if (!ensureValidDraft()) return;
    setHasEditedSchedule(true);
    if (scheduleError) return;

    // Only video drafts can be scheduled today; the footer hides Schedule for
    // other locations, so a non-bot intent here means a stale UI state.
    const intent = buildCreateSessionLaunchIntent(draft);
    if (intent.action !== CREATE_SESSION_LAUNCH_ACTIONS.CREATE_BOT) return;
    const meetingURL = intent.payload?.meetingURL;
    if (
      !isSupportedVideoCallLink(meetingURL) ||
      !canScheduleVideoCallLink(meetingURL)
    ) {
      toast({
        title: 'Could not schedule encounter',
        description: 'Enter a schedulable video call link.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const joinAt = new Date(scheduleJoinAtLocal);
    setIsScheduling(true);
    try {
      const data = await createBot({
        ...intent.payload,
        joinAt: joinAt.toISOString(),
      });
      if (data?.success) {
        toast({
          title: 'Encounter scheduled',
          description: `The assistant will join the meeting at ${joinAt.toLocaleString()}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        onScheduled?.();
        onScheduleClose();
      }
    } catch (error) {
      toast({
        title: 'Could not schedule encounter',
        description: error?.message || 'Try again.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleScheduleDateChange = (event: any) => {
    setHasEditedSchedule(true);
    const nextDate = event.target.value;
    const nextTime =
      scheduledTimeKey ||
      splitJoinAtLocal(formatForDatetimeLocal(defaultCreateSessionJoinAt()))
        .time;
    setScheduleJoinAtLocal(combineJoinAtLocal(nextDate, nextTime));
  };

  const handleScheduleTimeChange = (event: any) => {
    setHasEditedSchedule(true);
    const nextTime = event.target.value;
    const nextDate =
      scheduledDateKey ||
      splitJoinAtLocal(formatForDatetimeLocal(defaultCreateSessionJoinAt()))
        .date;
    setScheduleJoinAtLocal(combineJoinAtLocal(nextDate, nextTime));
  };

  return {
    dateOptions,
    handleScheduleDateChange,
    handleScheduleSubmit,
    handleScheduleTimeChange,
    isScheduling,
    openScheduleModal,
    scheduledDateKey,
    scheduledTimeKey,
    userTimezoneLabel,
    visibleScheduleError,
  };
}
